import React from "react";
import { ShieldCheck, AlertCircle, ArrowRight, TrendingUp, BadgeCheck } from "lucide-react";

function verdictTone(score) {
  if (score >= 75) return { color: "hsl(145 40% 32%)", ring: "hsl(145 40% 45%)", icon: ShieldCheck };
  if (score >= 60) return { color: "hsl(38 55% 30%)", ring: "hsl(38 70% 50%)", icon: ShieldCheck };
  return { color: "hsl(15 62% 35%)", ring: "hsl(15 62% 57%)", icon: AlertCircle };
}

function CircularScore({ score, label, size = 76, testId }) {
  const t = verdictTone(score);
  const r = (size / 2) - 6;
  const circumference = 2 * Math.PI * r;
  const dash = (score / 100) * circumference;
  return (
    <div className="flex flex-col items-center gap-1.5" data-testid={testId}>
      <div className="relative" style={{ width: size, height: size }}>
        <svg viewBox={`0 0 ${size} ${size}`} className="-rotate-90" style={{ width: size, height: size }}>
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="hsl(var(--muted))" strokeWidth="6" />
          <circle
            cx={size / 2} cy={size / 2} r={r} fill="none"
            stroke={t.ring} strokeWidth="6" strokeLinecap="round"
            strokeDasharray={`${dash} ${circumference}`}
            style={{ transition: "stroke-dasharray 800ms ease-out" }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="font-display text-xl font-bold" style={{ color: t.color }}>{score}</span>
        </div>
      </div>
      {label && <div className="label-eyebrow text-[10px]">{label}</div>}
    </div>
  );
}

export default function NaturalnessScore({ data, before }) {
  if (!data || typeof data.score !== "number") return null;
  const { score, verdict, breakdown = [] } = data;
  const beforeScore = before && typeof before.score === "number" ? before.score : null;
  const delta = beforeScore != null ? score - beforeScore : null;
  const t = verdictTone(score);
  const Icon = t.icon;

  return (
    <div data-testid="naturalness-score" className="surface p-5 md:p-6 mt-6 fade-up">
      <div className="flex flex-col md:flex-row md:items-center gap-6">
        {/* Score gauges */}
        <div className="flex items-center gap-3">
          {beforeScore != null && (
            <>
              <CircularScore score={beforeScore} label="Before" size={64} testId="score-before" />
              <ArrowRight size={16} className="text-[hsl(var(--muted-foreground))] shrink-0" />
            </>
          )}
          <CircularScore score={score} label={beforeScore != null ? "After" : undefined} size={beforeScore != null ? 76 : 80} testId="score-after" />
          <div className="min-w-0">
            <div className="label-eyebrow mb-1 flex items-center gap-1.5">
              <Icon size={12} style={{ color: t.ring }} /> Humanly Naturalness Score
            </div>
            <div className="font-display text-lg leading-tight" style={{ color: t.color }}>
              {verdict}
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-1.5">
              {score >= 90 && (
                <div
                  data-testid="passes-detection-badge"
                  className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full fade-up"
                  style={{ background: "hsl(145 40% 92%)", color: "hsl(145 45% 25%)", border: "1px solid hsl(145 40% 55%)" }}
                  title="Humanly's own measurement: this text has the perplexity, rhythm, and phrasing patterns detectors associate with human writing. It is an indicator, not a guarantee against any specific detector."
                >
                  <BadgeCheck size={12} /> Low detection risk
                </div>
              )}
              {delta != null && delta > 0 && (
                <div
                  data-testid="score-delta"
                  className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full"
                  style={{ background: "hsl(145 40% 94%)", color: "hsl(145 40% 32%)" }}
                >
                  <TrendingUp size={11} /> +{delta} points
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Breakdown bars */}
        <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 md:pl-6 md:border-l md:border-[hsl(var(--border))]">
          {breakdown.map((b) => (
            <div key={b.label} className="min-w-0" data-testid={`breakdown-${b.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <div className="flex items-baseline justify-between gap-3 mb-1.5">
                <span className="text-xs text-[hsl(var(--muted-foreground))] leading-tight">{b.label}</span>
                <span className="text-[11px] font-mono-editor tabular-nums text-[hsl(var(--muted-foreground))] shrink-0">
                  {b.value}
                </span>
              </div>
              <div className="h-1.5 w-full bg-[hsl(var(--muted))] rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${b.value}%`,
                    background: b.value >= 60 ? "hsl(145 40% 45%)" : b.value >= 30 ? "hsl(38 70% 50%)" : "hsl(15 62% 57%)",
                    transition: "width 700ms ease-out",
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Small inline badge for lists (History page)
export function ScorePill({ score, testId }) {
  if (typeof score !== "number") return null;
  const t = verdictTone(score);
  const passes = score >= 85;
  return (
    <span
      data-testid={testId}
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold"
      style={{ background: "hsl(45 60% 96%)", color: t.color, border: `1px solid ${t.ring}` }}
    >
      {passes ? (
        <BadgeCheck size={11} style={{ color: t.ring }} />
      ) : (
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: t.ring }} />
      )}
      {score}
    </span>
  );
}
